<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="receive-products-modal"
    :title="modalTitle"
    hide-footer
    @hidden="resetModal"
  >
    <div
      v-if="canCreateProducts"
      class="d-flex justify-content-end mb-1"
    >
      <b-button
        variant="primary"
        size="sm"
        :disabled="isAnyReceiving"
        @click="openUnitModal"
      >
        <span>Create new product</span>
      </b-button>
    </div>

    <BarcodeTargetField
      wrapper-class="mb-2"
      hint-key="receive-products-search"
      :active="showModal"
    >
      <b-form-input
        v-model="searchKeyword"
        placeholder="Search by id, name, SKU or barcode"
        :disabled="isAnyReceiving"
        class="barcode-target"
      />
    </BarcodeTargetField>

    <div
      v-if="isProductSearchLoading"
      class="text-muted small mb-2"
    >
      Searching...
    </div>
    <div
      v-else-if="showNoProductsFound"
      class="text-muted mb-2"
    >
      No products found.
    </div>

    <template v-if="productRows.length > 0">
      <div class="receive-products-head mb-1 p-1 border rounded receive-desktop-only">
        <b-row class="align-items-center font-weight-bold">
          <b-col cols="1">&nbsp;</b-col>
          <b-col cols="2">Product</b-col>
          <b-col cols="1" class="receive-col-id">ID</b-col>
          <b-col cols="1">Price</b-col>
          <b-col cols="3">Supplier</b-col>
          <b-col cols="2" class="receive-col-qty">Quantity</b-col>
          <b-col cols="2" class="receive-col-action">&nbsp;</b-col>
        </b-row>
      </div>

      <div
        v-for="product in productRows"
        :key="product.id"
        class="mb-1 border rounded p-1 receive-product-row"
      >
        <!-- Desktop layout -->
        <b-row class="align-items-start receive-desktop-only">
          <b-col cols="1">
            <img
              :src="productPictureSrc(product)"
              alt=""
              class="receive-product-img"
            >
          </b-col>
          <b-col cols="2">
            <span class="receive-product-name">{{ product.name }}</span>
          </b-col>
          <b-col cols="1" class="receive-col-id">
            <span class="receive-product-id">{{ product.id }}</span>
          </b-col>
          <b-col cols="1">
            <span>{{ $formatCurrency(product.price, null) }}</span>
          </b-col>
          <b-col cols="3">
            <b-form-input
              :value="product.supplier_search"
              size="sm"
              placeholder="Search supplier"
              autocomplete="off"
              :disabled="product.receiving"
              @input="onSupplierSearchInput(product, $event)"
            />
            <div
              v-if="product.selected_supplier"
              class="receive-supplier-selected d-flex align-items-center mt-1"
            >
              <div
                class="receive-supplier-thumb"
                :class="{ 'receive-supplier-thumb--empty': !product.selected_supplier.image }"
              >
                <img
                  v-if="product.selected_supplier.image"
                  :src="pictureUrl(product.selected_supplier.image)"
                  alt=""
                  class="receive-supplier-img"
                >
              </div>
              <span class="ml-1 flex-grow-1 text-truncate">{{ product.selected_supplier.name }}</span>
              <b-button
                size="sm"
                variant="link"
                class="p-0"
                :disabled="product.receiving"
                @click="clearSupplier(product)"
              >
                Clear
              </b-button>
            </div>
            <div
              v-else-if="product.supplier_search_loading"
              class="text-muted small mt-1"
            >
              Searching...
            </div>
            <ul
              v-else-if="product.supplier_results.length > 0"
              class="receive-supplier-results list-unstyled mb-0 mt-1"
            >
              <li
                v-for="supplier in product.supplier_results"
                :key="supplier.id"
                class="receive-supplier-result-item d-flex align-items-center"
                @click="selectSupplier(product, supplier)"
              >
                <div
                  class="receive-supplier-thumb"
                  :class="{ 'receive-supplier-thumb--empty': !supplier.image }"
                >
                  <img
                    v-if="supplier.image"
                    :src="pictureUrl(supplier.image)"
                    alt=""
                    class="receive-supplier-img"
                  >
                </div>
                <span class="ml-1 text-truncate">{{ supplier.name }}</span>
              </li>
            </ul>
          </b-col>
          <b-col cols="2" class="receive-col-qty">
            <b-form-input
              :value="product.receive_quantity"
              type="number"
              min="1"
              step="1"
              placeholder="Qty"
              inputmode="numeric"
              class="receive-qty-input"
              :disabled="product.receiving"
              @keydown="onQuantityKeydown"
              @input="setQuantity(product, $event)"
            />
          </b-col>
          <b-col cols="2" class="receive-col-action p-0">
            <b-button
              variant="primary"
              size="sm"
              block
              class="receive-action-btn"
              :disabled="product.receiving"
              @click="receiveProduct(product)"
            >
              <b-spinner v-if="product.receiving" small class="mr-25" />
              <span>{{ receiveActionLabel(product) }}</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- Mobile compact layout -->
        <div class="receive-mobile-only receive-mobile-row">
          <img
            :src="productPictureSrc(product)"
            alt=""
            class="receive-product-img receive-mobile-img"
          >
          <div class="receive-mobile-main">
            <span class="receive-product-name">{{ product.name }}</span>
            <div class="receive-mobile-actions mt-50">
              <b-button
                variant="outline-primary"
                size="sm"
                class="receive-details-btn"
                :disabled="product.receiving"
                @click="openProductDetails(product)"
              >
                Quantity &amp; Supplier
              </b-button>
              <b-button
                v-if="product.receive_ready"
                variant="primary"
                size="sm"
                class="receive-action-btn mt-50"
                :disabled="product.receiving"
                @click="receiveProduct(product)"
              >
                <b-spinner v-if="product.receiving" small class="mr-25" />
                <span>{{ receiveActionLabel(product) }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </b-modal>

  <b-modal
    v-model="showDetailsModal"
    size="md"
    id="receive-product-details-modal"
    title="Product details"
    hide-footer
    no-close-on-backdrop
    @hidden="onDetailsModalHidden"
  >
    <template v-if="detailsProduct">
      <div class="receive-details-rows">
        <div class="receive-details-row">
          <div class="receive-details-label">ID</div>
          <div class="receive-details-value">{{ detailsProduct.id }}</div>
        </div>
        <div class="receive-details-row">
          <div class="receive-details-label">Price</div>
          <div class="receive-details-value">{{ $formatCurrency(detailsProduct.price, null) }}</div>
        </div>
        <div class="receive-details-row">
          <div class="receive-details-label">Supplier</div>
          <div class="receive-details-value">
            <b-form-input
              :value="detailsProduct.supplier_search"
              size="sm"
              placeholder="Search supplier"
              autocomplete="off"
              :disabled="detailsProduct.receiving"
              @input="onSupplierSearchInput(detailsProduct, $event)"
            />
            <div
              v-if="detailsProduct.selected_supplier"
              class="receive-supplier-selected d-flex align-items-center mt-1"
            >
              <div
                class="receive-supplier-thumb"
                :class="{ 'receive-supplier-thumb--empty': !detailsProduct.selected_supplier.image }"
              >
                <img
                  v-if="detailsProduct.selected_supplier.image"
                  :src="pictureUrl(detailsProduct.selected_supplier.image)"
                  alt=""
                  class="receive-supplier-img"
                >
              </div>
              <span class="ml-1 flex-grow-1 text-truncate">{{ detailsProduct.selected_supplier.name }}</span>
              <b-button
                size="sm"
                variant="link"
                class="p-0"
                :disabled="detailsProduct.receiving"
                @click="clearSupplier(detailsProduct)"
              >
                Clear
              </b-button>
            </div>
            <div
              v-else-if="detailsProduct.supplier_search_loading"
              class="text-muted small mt-1"
            >
              Searching...
            </div>
            <ul
              v-else-if="detailsProduct.supplier_results.length > 0"
              class="receive-supplier-results list-unstyled mb-0 mt-1"
            >
              <li
                v-for="supplier in detailsProduct.supplier_results"
                :key="supplier.id"
                class="receive-supplier-result-item d-flex align-items-center"
                @click="selectSupplier(detailsProduct, supplier)"
              >
                <div
                  class="receive-supplier-thumb"
                  :class="{ 'receive-supplier-thumb--empty': !supplier.image }"
                >
                  <img
                    v-if="supplier.image"
                    :src="pictureUrl(supplier.image)"
                    alt=""
                    class="receive-supplier-img"
                  >
                </div>
                <span class="ml-1 text-truncate">{{ supplier.name }}</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="receive-details-row">
          <div class="receive-details-label">Quantity</div>
          <div class="receive-details-value">
            <b-form-input
              :value="detailsProduct.receive_quantity"
              type="number"
              min="1"
              step="1"
              placeholder="Qty"
              inputmode="numeric"
              class="receive-qty-input"
              :disabled="detailsProduct.receiving"
              @keydown="onQuantityKeydown"
              @input="setQuantity(detailsProduct, $event)"
            />
          </div>
        </div>
      </div>
      <b-button
        variant="primary"
        block
        class="mt-2"
        :disabled="detailsProduct.receiving"
        @click="confirmProductDetails"
      >
        Done
      </b-button>
    </template>
  </b-modal>

  <ManageUnit
    :click-date="unitClickDate"
    :unit="null"
    :default-stock-management="true"
    @unit-created="onUnitCreated"
  />
</template>

<script>
import {
  BButton,
  BCol,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import { canStaffCreate } from '@/libs/staffPermissions'
import ManageUnit from '@/views/Components/ManageUnit.vue'
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  components: {
    ManageUnit,
    BarcodeTargetField,
    BModal,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BSpinner,
  },
  props: {
    stockLocation: {
      type: Object,
      default: null,
    },
    clickDate: {
      type: Date,
      default: null,
    },
  },
  data() {
    return {
      showModal: false,
      showDetailsModal: false,
      detailsProduct: null,
      searchKeyword: '',
      productRows: [],
      searchTimer: null,
      isProductSearchLoading: false,
      searchCompletedKeyword: '',
      supplierSearchTimers: {},
      unitClickDate: null,
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'auth/user',
    }),
    canCreateProducts() {
      return canStaffCreate(this.authUser, 'products')
    },
    modalTitle() {
      if (!this.stockLocation?.name) {
        return 'Receive Products'
      }
      return `Receive Products — ${this.stockLocation.name}`
    },
    isAnyReceiving() {
      return this.productRows.some((row) => row.receiving)
    },
    showNoProductsFound() {
      const trimmed = String(this.searchKeyword || '').trim()
      return trimmed.length >= 1
        && !this.isProductSearchLoading
        && this.searchCompletedKeyword === trimmed
        && this.productRows.length === 0
    },
  },
  watch: {
    clickDate() {
      if (!this.stockLocation?.id) {
        return
      }
      this.resetModal()
      this.showModal = true
    },
    searchKeyword(keyword) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }

      const trimmed = String(keyword || '').trim()
      if (trimmed.length < 1) {
        this.productRows = []
        this.isProductSearchLoading = false
        this.searchCompletedKeyword = ''
        return
      }

      this.isProductSearchLoading = true
      this.searchTimer = setTimeout(() => {
        this.searchUnit({
          keyword: trimmed,
          platformId: 0,
          forReceive: true,
        })
          .then((response) => {
            this.productRows = this.parseSearchUnits(response).map((product) => this.mapProductRow(product))
            this.searchCompletedKeyword = trimmed
          })
          .catch(() => {
            this.productRows = []
            this.searchCompletedKeyword = trimmed
          })
          .finally(() => {
            this.isProductSearchLoading = false
          })
      }, 300)
    },
  },
  methods: {
    ...mapActions({
      searchUnit: 'units/searchUnit',
      storeStockChanges: 'stockChanges/storeStockChanges',
      searchSuppliers: 'suppliers/searchSuppliers',
    }),
    mapProductRow(product) {
      return {
        ...product,
        receive_quantity: '',
        receive_ready: false,
        has_received: false,
        receiving: false,
        supplier_search: '',
        supplier_results: [],
        supplier_search_loading: false,
        selected_supplier: null,
      }
    },
    receiveActionLabel(product) {
      return product.has_received ? 'Receive again' : 'Receive'
    },
    resetReceiveFields(product) {
      product.receive_quantity = ''
      product.receive_ready = false
      product.selected_supplier = null
      product.supplier_search = ''
      product.supplier_results = []
      product.supplier_search_loading = false
      if (this.supplierSearchTimers[product.id]) {
        clearTimeout(this.supplierSearchTimers[product.id])
        delete this.supplierSearchTimers[product.id]
      }
    },
    parseSearchUnits(response) {
      if (Array.isArray(response)) {
        return response
      }
      if (Array.isArray(response?.data)) {
        return response.data
      }
      return []
    },
    normalizePicture(picture) {
      if (Array.isArray(picture) && picture[0]?.src) {
        return picture
      }
      if (typeof picture === 'string' && picture.trim() !== '') {
        try {
          const parsed = JSON.parse(picture)
          if (Array.isArray(parsed) && parsed[0]?.src) {
            return parsed
          }
        } catch (e) {
          // not JSON
        }
      }
      return [{ name: '', src: NO_IMAGE }]
    },
    productPictureSrc(product) {
      const picture = this.normalizePicture(product?.picture)
      return picture[0]?.src || NO_IMAGE
    },
    pictureUrl(picture) {
      if (!picture) {
        return ''
      }
      const p = String(picture).trim()
      if (!p) {
        return ''
      }
      if (/^https?:\/\//i.test(p) || p.startsWith('//')) {
        return p
      }
      return p.startsWith('/') ? p : `/${p}`
    },
    inputValue(raw) {
      if (raw && typeof raw === 'object' && raw.target != null) {
        return String(raw.target.value ?? '')
      }
      if (raw !== null && raw !== undefined) {
        return String(raw)
      }
      return ''
    },
    onSupplierSearchInput(product, raw) {
      const value = this.inputValue(raw)
      const keyword = value.trim()
      product.supplier_search = value

      if (
        product.selected_supplier
        && keyword !== String(product.selected_supplier.name || '').trim()
      ) {
        product.selected_supplier = null
      }

      if (this.supplierSearchTimers[product.id]) {
        clearTimeout(this.supplierSearchTimers[product.id])
      }

      if (keyword.length < 2) {
        product.supplier_results = []
        product.supplier_search_loading = false
        return
      }

      product.supplier_search_loading = true
      this.supplierSearchTimers[product.id] = setTimeout(() => {
        this.searchSuppliers(keyword)
          .then((results) => {
            product.supplier_results = results || []
          })
          .catch(() => {
            product.supplier_results = []
          })
          .finally(() => {
            product.supplier_search_loading = false
          })
      }, 300)
    },
    selectSupplier(product, supplier) {
      product.selected_supplier = { ...supplier }
      product.supplier_search = supplier.name
      product.supplier_results = []
    },
    clearSupplier(product) {
      product.selected_supplier = null
      product.supplier_search = ''
      product.supplier_results = []
    },
    parseQuantity(value) {
      if (value === '' || value === null || value === undefined) {
        return 0
      }
      const n = parseInt(String(value), 10)
      return Number.isNaN(n) || n < 0 ? 0 : n
    },
    syncReceiveReady(product) {
      product.receive_ready = this.parseQuantity(product.receive_quantity) >= 1
    },
    setQuantity(product, raw) {
      let str = ''
      if (raw && typeof raw === 'object' && raw.target != null) {
        str = String(raw.target.value)
      } else if (raw !== null && raw !== undefined && raw !== '') {
        str = String(raw)
      }

      if (str === '') {
        product.receive_quantity = ''
        product.receive_ready = false
        return
      }

      const parsed = parseInt(str, 10)
      if (Number.isNaN(parsed) || parsed < 0) {
        product.receive_quantity = ''
        product.receive_ready = false
        return
      }

      product.receive_quantity = String(parsed)

      // Mobile: Receive only appears after Done. Clearing qty hides it immediately.
      if (product.receive_ready) {
        this.syncReceiveReady(product)
      }
    },
    onQuantityKeydown(e) {
      if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
        e.preventDefault()
      }
    },
    openProductDetails(product) {
      this.detailsProduct = product
      this.showDetailsModal = true
    },
    confirmProductDetails() {
      if (!this.detailsProduct) {
        return
      }
      this.syncReceiveReady(this.detailsProduct)
      this.showDetailsModal = false
    },
    onDetailsModalHidden() {
      this.detailsProduct = null
    },
    async receiveProduct(product) {
      const quantity = this.parseQuantity(product.receive_quantity)
      if (quantity < 1) {
        toastFailure('Please enter a quantity of at least 1.')
        product.receive_ready = false
        return
      }

      product.receiving = true
      try {
        const stockPayload = {
          stock_location_id: this.stockLocation.id,
          in_quantity: quantity,
          out_quantity: 0,
        }

        if (product.selected_supplier?.id) {
          stockPayload.supplier_id = product.selected_supplier.id
        }

        await this.storeStockChanges({
          unitId: product.id,
          stocks: [stockPayload],
        })
        product.has_received = true
        this.resetReceiveFields(product)
        toastSuccess('Stock updated successfully.')
      } catch (error) {
        toastFailure(backendErrorString(error) || 'Failed to receive products.')
      } finally {
        product.receiving = false
      }
    },
    openUnitModal() {
      this.unitClickDate = new Date()
    },
    onUnitCreated(unit) {
      if (!unit?.id) {
        return
      }
      this.searchKeyword = String(unit.id)
    },
    resetModal() {
      this.searchKeyword = ''
      this.productRows = []
      this.isProductSearchLoading = false
      this.searchCompletedKeyword = ''
      this.unitClickDate = null
      this.showDetailsModal = false
      this.detailsProduct = null
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      Object.values(this.supplierSearchTimers).forEach((timer) => {
        clearTimeout(timer)
      })
      this.supplierSearchTimers = {}
    },
  },
}
</script>

<style scoped>
.receive-products-head {
  background: #f8f8f8;
}

.receive-product-img {
  width: 100%;
  max-width: 64px;
  border: 1px solid #ddd;
  object-fit: contain;
}

.receive-col-id {
  flex: 0 0 3rem;
  max-width: 3rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.receive-product-id {
  font-size: 0.8125rem;
  word-break: break-all;
  line-height: 1.2;
}

.receive-product-name {
  display: block;
  line-height: 1.3;
}

.receive-col-qty {
  min-width: 5.5rem;
}

.receive-qty-input {
  min-width: 100%;
}

.receive-col-action {
  min-width: 6.5rem;
}

.receive-action-btn {
  white-space: nowrap;
}

.receive-mobile-only {
  display: none;
}

.receive-mobile-row {
  align-items: flex-start;
  gap: 0.75rem;
}

.receive-mobile-img {
  width: 64px;
  max-width: 64px;
  flex-shrink: 0;
}

.receive-mobile-main {
  flex: 1;
  min-width: 0;
}

.receive-mobile-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.receive-details-rows {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.receive-details-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6e6b7b;
  margin-bottom: 0.25rem;
}

.receive-details-value {
  color: #5e5873;
  word-break: break-word;
}

.receive-supplier-results {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  background: #fff;
}

.receive-supplier-result-item {
  padding: 0.35rem 0.5rem;
  cursor: pointer;
}

.receive-supplier-result-item:hover {
  background: #f5f5f5;
}

.receive-supplier-thumb {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
}

.receive-supplier-thumb--empty {
  background: #f0f0f0;
}

.receive-supplier-img {
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
}

@media (max-width: 767.98px) {
  .receive-desktop-only {
    display: none !important;
  }

  .receive-mobile-only {
    display: flex !important;
  }
}
</style>
